import React from "react";
import { Box, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Axios from "axios";
import TextEditor from "./TextEditor";
import getCookie from "../../../utils/getCookies";

const MainMemoComp = ({
    userId,
    socket,
    colors,
    mainMemo,
    setMainMemo,
    enqueueSnackbar,
    handleLogout,
    isThisAdminInViewMode,
//   mainMemoUpdated,
//   setMainMemoUpdated,
}) => {

    React.useEffect(() => {
        async function fetchMainMemos() {
            try {
                const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/weekly/main-memos`,{
                    headers: {
                        Authorization: `Bearer ${getCookie('_auth')}`,
                    },
                });

                setMainMemo(res.data);
                // setMainMemoUpdated(false);
            } catch (err) {
                if (err.response?.status === 401) {
                    handleLogout();
                } else {
                    const error_msg =
                        err.response?.data?.message ||
                        err.response?.data?.error ||
                        "Error fetching main memos";
                    enqueueSnackbar(error_msg, { variant: "error" });
                }
            }
        }

        fetchMainMemos();
    }, []);

    // Handle adding a new memo at the end
    const handleAddMainMemo = async () => {
        try {
            if (isThisAdminInViewMode) {
                enqueueSnackbar("You are in view mode.", { variant: "info" });
                return
            };

            socket.emit("post.WeeklyMainMemos", {
                index: mainMemo.length + 1,
            });
        } catch (err) {
            console.log(err);
        }
    };

    // Handle inserting a new memo BEFORE an existing one at the given 1-based index
    const handleInsertMainMemo = (insertIndex) => {
        try {
            if (isThisAdminInViewMode) {
                enqueueSnackbar("You are in view mode.", { variant: "info" });
                return;
            }

            socket.emit("insert.WeeklyMainMemos", {
                index: insertIndex,
            });
        } catch (err) {
            console.log(err);
        }
    };

    // Handle reordering via drag and drop
    const handleDragEnd = (result) => {
        try {
            if (!result.destination) return;

            if (isThisAdminInViewMode) {
                enqueueSnackbar("You are in view mode.", { variant: "info" });
                return;
            }

            const fromIdx = result.source.index;
            const toIdx = result.destination.index;
            if (fromIdx === toIdx) return;

            // Optimistically reorder locally, server broadcast will re-sync the re-indexed list
            const reordered = Array.from(mainMemo);
            const [moved] = reordered.splice(fromIdx, 1);
            reordered.splice(toIdx, 0, moved);
            setMainMemo(reordered);

            socket.emit("reorder.WeeklyMainMemos", {
                ordered_ids: reordered.map((m) => m.id),
            });
        } catch (err) {
            console.log(err);
        }
    };

    // Small "+" that lives on the drag-handle bar. Stops mousedown from reaching
    // the dnd sensor so clicking it inserts a memo instead of starting a drag.
    const HandleInsertButton = ({ onClick, title }) => (
        <Tooltip title={title} placement="top">
            <Box
                component="span"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={onClick}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    cursor: "pointer",
                    color: colors.grey[300],
                    "&:hover": { backgroundColor: colors.grey[600], color: "#fff" },
                }}
            >
                <AddIcon sx={{ fontSize: 15 }} />
            </Box>
        </Tooltip>
    );

    if (mainMemo.length === 0) {
        return (
            <Box
                sx={{
                    position: "absolute",
                    left: "100px",
                    top: "300px",
                    zIndex: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: "lightgray",
                    borderRadius: "50%",
                    padding: 1,
                }}
                onClick={handleAddMainMemo}
            >
                <AddIcon fontSize="large" sx={{ color: "black" }} />
            </Box>
        );
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="mainMemos" direction="horizontal">
                {(dropProvided) => (
                    <Box
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        sx={{ display: "flex", alignItems: "flex-start" }}
                    >
                        {mainMemo.map((memoBox, idx) => (
                            <Draggable
                                key={memoBox.id}
                                draggableId={String(memoBox.id)}
                                index={idx}
                            >
                                {(dragProvided, snapshot) => (
                                    <Box
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        {/* Drag handle bar: grip in the center, insert-left / insert-right on the ends */}
                                        <Box
                                            {...dragProvided.dragHandleProps}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                height: 22,
                                                px: "4px",
                                                border: "1px solid #ccc",
                                                borderBottom: "none",
                                                backgroundColor: snapshot.isDragging
                                                    ? colors.grey[700]
                                                    : colors.grey[900],
                                                cursor: "grab",
                                                "&:active": { cursor: "grabbing" },
                                            }}
                                        >
                                            <HandleInsertButton
                                                title="Insert memo to the left"
                                                onClick={() =>
                                                    handleInsertMainMemo(memoBox.index ?? idx + 1)
                                                }
                                            />
                                            <Tooltip title="Drag to reorder" placement="top">
                                                <DragIndicatorIcon
                                                    fontSize="small"
                                                    sx={{ color: colors.grey[300], transform: "rotate(90deg)" }}
                                                />
                                            </Tooltip>
                                            <HandleInsertButton
                                                title="Insert memo to the right"
                                                onClick={() =>
                                                    handleInsertMainMemo((memoBox.index ?? idx + 1) + 1)
                                                }
                                            />
                                        </Box>

                                        <TextEditor
                                            memo={memoBox.memo}
                                            memoId={memoBox.id}
                                            socket={socket}
                                            userId={userId}
                                            activeUsersNum={memoBox.active_users_num}
                                            colors={colors}
                                            isThisAdminInViewMode={isThisAdminInViewMode}
                                        />
                                    </Box>
                                )}
                            </Draggable>
                        ))}
                        {dropProvided.placeholder}
                    </Box>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default MainMemoComp;
